/*
 *   Copyright (c) 2026 vlinder Labs Private Limited
 *   All rights reserved.
 *   This software is proprietary of Vlinder Labs Pvt. Ltd and can not be distributed / used for any purpose outside of Vlinder Labs Pvt. Ltd without explicit written consent from the copyright owner.
 */

import React, { useEffect, useState } from "react";
import { CostumePageService } from "../../services/costumePageService";

export interface IHeldTicketsPanelProps {
  boxId?: string;
  // Called with the HELD TicketOrder to pay with this cash entry, or null.
  onChange: (order: any | null) => void;
}

/**
 * Staff cash entry (#736): lists the Box's HELD tickets. When ticked, they are paid
 * in full (incl. Convenience fee) together with the costume amount, in Cash.
 */
export const HeldTicketsPanel: React.FC<IHeldTicketsPanelProps> = ({
  boxId,
  onChange,
}) => {
  const [order, setOrder] = useState<any>(null);
  const [include, setInclude] = useState(true);

  useEffect(() => {
    let alive = true;
    setOrder(null);
    onChange(null);
    if (!boxId) return;
    CostumePageService.getInstance()
      .getBoxTicketOrders(boxId)
      .then((orders) => {
        const held = (orders || []).find(
          (o: any) =>
            o?.status === "HELD" &&
            (!o?.holdExpiresAt ||
              new Date(o.holdExpiresAt).getTime() > Date.now())
        );
        if (alive && held) {
          setOrder(held);
          setInclude(true);
          onChange(held);
        }
      })
      .catch((err) => console.warn("Could not load the Box's tickets:", err));
    return () => {
      alive = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [boxId]);

  if (!order) return null;
  const total = Number(order.total) || 0;

  return (
    <div
      className="mb-3 p-2"
      style={{ border: "1px solid #dee2e6", borderRadius: "6px" }}
      data-testid="held-tickets-panel"
    >
      <div style={{ fontWeight: 600 }}>
        Event Tickets – {order.feteName || "Fete"}
      </div>
      {(order.lines || []).map((l: any) => (
        <div
          key={l.tierId}
          className="d-flex justify-content-between"
          style={{ fontSize: "14px" }}
        >
          <span>
            {l.tierName} × {l.quantity}
          </span>
          <span>TT$ {Number(l.lineTotal || 0).toFixed(2)}</span>
        </div>
      ))}
      {Number(order.convenienceFee) > 0 && (
        <div
          className="d-flex justify-content-between"
          style={{ fontSize: "14px" }}
        >
          <span>Convenience Fee</span>
          <span>TT$ {Number(order.convenienceFee).toFixed(2)}</span>
        </div>
      )}
      <div className="d-flex justify-content-between" style={{ fontWeight: 600 }}>
        <span>Tickets Total (paid in full)</span>
        <span>TT$ {total.toFixed(2)}</span>
      </div>
      <label className="mt-1" style={{ fontSize: "14px" }}>
        <input
          type="checkbox"
          checked={include}
          onChange={(e) => {
            setInclude(e.target.checked);
            onChange(e.target.checked ? order : null);
          }}
          style={{ marginRight: "6px" }}
        />
        Take TT$ {total.toFixed(2)} cash for these tickets with this payment (Cash
        only)
      </label>
    </div>
  );
};
